/**
 * Shared service selector modal used in Dashboard ("Pay for services")
 * and WalletPay page. Fetches live UCH services from pay1one.com API.
 */
import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import {
  FaTimes, FaSearch, FaChevronDown, FaPlus, FaMinus,
  FaCheck, FaSpinner, FaShoppingBag, FaArrowRight
} from "react-icons/fa";
import axios from "axios";

const fetchDepts = async () => {
  const res = await axios.get("https://pay1one.com/api/get/203/hospital-dept");
  if (!res.data.success) throw new Error("Failed to fetch departments");
  return res.data.data.orgDepts;
};

const fetchServices = async ({ dept, search }) => {
  if (search && search.trim()) {
    const url = dept
      ? `https://pay1one.com/api/get/203/search-hospital-records?query=${encodeURIComponent(search)}&department_id=${dept}`
      : `https://pay1one.com/api/get/203/search-hospital-records?query=${encodeURIComponent(search)}`;
    const res = await axios.get(url);
    if (!res.data.success) return [];
    return res.data.data;
  }
  if (dept) {
    const res = await axios.get(`https://pay1one.com/api/get/203/${dept}/hospital-revenuehead`);
    if (!res.data.success) return [];
    return res.data.data;
  }
  return [];
};

export default function ServiceSelectorModal({ open, onClose, redirectTo = "/wallet-pay" }) {
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const dropdownRef = useRef(null);

  const [dept, setDept] = useState("");
  const [deptLabel, setDeptLabel] = useState("All Departments");
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptSearch, setDeptSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [selected, setSelected] = useState({});   // { [id]: svc }
  const [quantities, setQuantities] = useState({}); // { [id]: number }
  const [customPrices, setCustomPrices] = useState({}); // { [id]: string }

  const { data: departments = [], isLoading: deptsLoading } = useQuery({
    queryKey: ["modal-depts"],
    queryFn: fetchDepts,
    enabled: open,
    staleTime: 15 * 60 * 1000,
  });

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["modal-services", dept, serviceSearch],
    queryFn: () => fetchServices({ dept, search: serviceSearch }),
    enabled: open && (!!dept || serviceSearch.trim().length > 1),
  });

  const filteredDepts = departments.filter(d =>
    d.name.toLowerCase().includes(deptSearch.toLowerCase())
  );

  const totalCost = Object.entries(selected).reduce((sum, [id, svc]) => {
    const price = parseFloat(customPrices[id] || svc.price || 0);
    const qty = quantities[id] || 1;
    return sum + price * qty;
  }, 0);
  const selectedCount = Object.keys(selected).length;

  const handleToggle = (svc) => {
    setSelected(prev => {
      if (prev[svc.id]) {
        const n = { ...prev }; delete n[svc.id]; return n;
      }
      return { ...prev, [svc.id]: svc };
    });
    if (!quantities[svc.id]) setQuantities(p => ({ ...p, [svc.id]: 1 }));
  };

  const handleQty = (id, delta, e) => {
    e.stopPropagation();
    setQuantities(p => ({ ...p, [id]: Math.max(1, (p[id] || 1) + delta) }));
  };

  const handleProceed = () => {
    if (!selectedCount) return;
    Object.entries(selected).forEach(([id, svc]) => {
      const price = parseFloat(customPrices[id] || svc.price || 0);
      const qty = quantities[id] || 1;
      addToCart({
        id: svc.id,
        name: svc.name,
        price,
        quantity: qty,
        description: typeof svc.description === "string" ? svc.description : "",
        department: svc.department?.name || svc.department || "",
        category: svc.category?.name || svc.category || "",
      });
    });
    onClose();
    navigate(redirectTo);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[88vh] rounded-t-3xl sm:rounded-2xl flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Select Hospital Service</h2>
            <p className="text-xs text-gray-500 mt-0.5">Search and add services to proceed with wallet payment</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition">
            <FaTimes className="text-gray-500 w-3.5 h-3.5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 space-y-2.5 shrink-0 border-b border-gray-50 bg-gray-50/50">
          {/* Department dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDeptOpen(!deptOpen)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm hover:border-green-400 transition text-left"
            >
              <FaSearch className="text-gray-400 w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 truncate text-gray-700">{deptLabel}</span>
              <FaChevronDown className={`text-gray-400 w-3 h-3 transition-transform shrink-0 ${deptOpen ? "rotate-180" : ""}`} />
            </button>
            {deptOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-56 overflow-hidden flex flex-col">
                <div className="p-2 border-b border-gray-50">
                  <input autoFocus type="text" placeholder="Search departments..." value={deptSearch}
                    onChange={e => setDeptSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500" />
                </div>
                <div className="overflow-y-auto">
                  <button onClick={() => { setDept(""); setDeptLabel("All Departments"); setDeptOpen(false); setDeptSearch(""); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 hover:text-[#0d3d2e] transition ${!dept ? "font-semibold text-[#0d3d2e] bg-green-50" : "text-gray-700"}`}>
                    All Departments
                  </button>
                  {deptsLoading ? (
                    <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2"><FaSpinner className="animate-spin" /> Loading...</div>
                  ) : filteredDepts.map(d => (
                    <button key={d.id} onClick={() => { setDept(d.id); setDeptLabel(d.name); setDeptOpen(false); setDeptSearch(""); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 hover:text-[#0d3d2e] transition border-t border-gray-50 ${dept === d.id ? "font-semibold text-[#0d3d2e] bg-green-50" : "text-gray-700"}`}>
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Service search */}
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input type="text" placeholder="Search service name or code..." value={serviceSearch}
              onChange={e => setServiceSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition placeholder-gray-400" />
          </div>
        </div>

        {/* Service list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!dept && !serviceSearch ? (
            <div className="text-center py-12 space-y-3">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto">
                <FaShoppingBag className="text-[#0d3d2e] w-7 h-7" />
              </div>
              <p className="text-gray-500 text-sm font-medium">Select a department or search for a service above</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {["Pharmacy", "Laboratory", "Radiology", "Consultations"].map(hint => (
                  <button key={hint} onClick={() => setServiceSearch(hint)}
                    className="text-xs bg-gray-100 hover:bg-green-50 hover:text-[#0d3d2e] text-gray-600 px-3 py-1.5 rounded-full transition">{hint}</button>
                ))}
              </div>
            </div>
          ) : servicesLoading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <FaSpinner className="animate-spin text-[#0d3d2e] w-5 h-5" />
              <span className="text-gray-500 text-sm">Loading services...</span>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No services found. Try a different search or department.
            </div>
          ) : (
            <div className="space-y-2">
              {services.map(svc => {
                const isSelected = !!selected[svc.id];
                const needsCustom = svc.fixed_price === "no" && parseFloat(svc.price || 0) === 0;
                const price = parseFloat(customPrices[svc.id] || svc.price || 0);
                const qty = quantities[svc.id] || 1;

                return (
                  <div key={svc.id} onClick={() => handleToggle(svc)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition border-2 ${
                      isSelected ? "border-[#0d3d2e] bg-green-50" : "border-transparent bg-gray-50 hover:bg-gray-100"
                    }`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${
                      isSelected ? "bg-[#0d3d2e] border-[#0d3d2e]" : "border-gray-300"
                    }`}>
                      {isSelected && <FaCheck className="text-white w-2.5 h-2.5" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{svc.name}</p>
                      <p className="text-xs text-gray-400 truncate">{svc.department?.name || svc.head_code || "General"}</p>
                      {needsCustom && isSelected ? (
                        <input type="number" placeholder="Enter amount (₦)" value={customPrices[svc.id] || ""}
                          onChange={e => { e.stopPropagation(); setCustomPrices(p => ({ ...p, [svc.id]: e.target.value })); }}
                          onClick={e => e.stopPropagation()}
                          className="mt-1 w-36 px-2.5 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-white" />
                      ) : (
                        <p className="text-xs font-semibold text-[#0d3d2e] mt-0.5">₦{price.toLocaleString()}</p>
                      )}
                    </div>

                    {isSelected && (
                      <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                        <button onClick={e => handleQty(svc.id, -1, e)}
                          className="w-7 h-7 rounded-full bg-white border border-gray-200 hover:border-[#0d3d2e] flex items-center justify-center transition">
                          <FaMinus className="w-2.5 h-2.5 text-gray-600" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold text-gray-900">{qty}</span>
                        <button onClick={e => handleQty(svc.id, 1, e)}
                          className="w-7 h-7 rounded-full bg-white border border-gray-200 hover:border-[#0d3d2e] flex items-center justify-center transition">
                          <FaPlus className="w-2.5 h-2.5 text-gray-600" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-100 px-5 py-4 bg-white">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500">{selectedCount} service{selectedCount !== 1 ? "s" : ""} selected</p>
              <p className="text-lg font-black text-gray-900">
                {totalCost > 0 ? `₦${totalCost.toLocaleString()}` : "—"}
              </p>
            </div>
            <button onClick={handleProceed} disabled={!selectedCount}
              className="flex items-center gap-2 bg-[#0d3d2e] hover:bg-[#0a3226] disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold px-6 py-3 rounded-xl transition text-sm disabled:cursor-not-allowed">
              Proceed to Pay <FaArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
